import { INewExportVersion } from "@kuika/kuika-cl-model";
import { useEffect, useState } from "react";
import { WorkFlowService } from "../../../../../services/workflow-service";
import { IWithSDProps, withSD } from "../../../../../shared/hoc/screen-design-consumer";
import { IWithContextProp, withContext } from "../../../../../shared/hoc/with-context";
import { KMainFunctions } from "../../../../../shared/utility/main-functions";
import { SaveStatus } from "../../../../../shared/utility/save-status";
import { DefaultModal } from "../../../../common/default-modal";
import KModalStepBody from "../../../../ui-kit/k-modal/k-modal-step-body";
import KuikaButton, { type KuikaButtonStatus } from "../../../../ui-kit/kuika-button/kuika-button";
import KuikaInfoBox from "../../../../ui-kit/kuika-info-box/kuika-info-box";
import KuikaInput from "../../../../ui-kit/kuika-input/kuika-input";
import KuikaSvg from "../../../../ui-kit/kuika-svg/kuika-svg";
import KuikaSmallText from "../../../../ui-kit/typography/SmallText/SmallText";
import "./workflow-version-details-modal.scss";

interface IVersionDraft {
  name: string;
  description: string;
}

interface IVersionDetailsFormProps {
  versions: INewExportVersion[];
  onDone: () => void;
}

// Kept as its own component so keystrokes re-render the form locally, independent of the
// surrounding DefaultModal's shouldComponentUpdate. Mounted fresh on each open (destroyOnClose).
// With more than one version the form becomes a stepper: one workflow per step, drafts kept
// across steps (keyed by versionId), a single Save on the last step persisting every filled draft.
const VersionDetailsForm = (props: IVersionDetailsFormProps): JSX.Element => {
  const [drafts, setDrafts] = useState<Record<string, IVersionDraft>>(() => {
    const initial: Record<string, IVersionDraft> = {};
    props.versions.forEach((version: INewExportVersion): void => {
      initial[version.versionId] = { name: "", description: "" };
    });
    return initial;
  });
  const [stepIndex, setStepIndex] = useState<number>(0);
  const [saveStatus, setSaveStatus] = useState<KuikaButtonStatus>("idle");
  // Save's state machine: the ✓ is held on Save, then the dialog closes. Created once, so every
  // render talks to the same machine; dropped with the dialog so a pending close cannot land late.
  const [saveMachine] = useState<SaveStatus>((): SaveStatus => new SaveStatus(setSaveStatus));
  useEffect((): (() => void) => (): void => saveMachine.dispose(), [saveMachine]);
  const isSaving: boolean = saveStatus === "loading";

  const isStepper: boolean = props.versions.length > 1;
  const isLastStep: boolean = stepIndex === props.versions.length - 1;
  const version: INewExportVersion = props.versions[stepIndex];
  const draft: IVersionDraft = drafts[version.versionId] ?? { name: "", description: "" };

  const updateDraft = (versionId: string, patch: Partial<IVersionDraft>): void => {
    setDrafts((prev: Record<string, IVersionDraft>) => ({
      ...prev,
      [versionId]: { ...prev[versionId], ...patch }
    }));
  };

  const handleSave = async (): Promise<void> => {
    if (!saveMachine.canStart) return;

    const toSave: INewExportVersion[] = props.versions.filter((v: INewExportVersion): boolean => {
      const versionDraft: IVersionDraft = drafts[v.versionId];
      return versionDraft && (versionDraft.name.trim().length > 0 || versionDraft.description.trim().length > 0);
    });

    // Nothing was filled in: there is nothing to confirm, so the dialog simply closes.
    if (toSave.length === 0) {
      props.onDone();
      return;
    }

    saveMachine.start();
    try {
      for (const v of toSave) {
        const versionDraft: IVersionDraft = drafts[v.versionId];
        await WorkFlowService.UpdateVersion({
          id: v.versionId,
          name: versionDraft.name.trim(),
          description: versionDraft.description.trim()
        });
      }
      saveMachine.succeed(props.onDone);
    } catch (error: unknown) {
      // The dialog stays open with its drafts, and Save says "Try again".
      saveMachine.fail();
      KMainFunctions.exceptionHandler(error);
    }
  };

  return (
    <>
      <div className="kuika-wfe-version-details__intro" data-testid="workflowVersionDetailsModal">
        This export created the workflow versions below. Add a name and description to keep a readable release history —
        or skip and edit them later from the workflow version panel.
      </div>

      {/* One workflow per step: the dialog's height follows the step it moves to. */}
      <KModalStepBody stepKey={version.versionId}>
        <div className="kuika-wfe-version-details__version">
          <div className="kuika-wfe-version-details__version-head">
            <span className="kuika-wfe-version-details__version-name">{version.workflowName}</span>
            <span className="kuika-wfe-version-details__version-number">v{version.versionNumber}</span>
            {isStepper && (
              <span className="kuika-wfe-version-details__step-counter">
                {stepIndex + 1} of {props.versions.length}
              </span>
            )}
          </div>
          <div className="kuika-wfe-version-details__field">
            <KuikaInput
              placeholder="Version name"
              value={draft.name}
              maxLength={120}
              onChange={(_e: React.ChangeEvent<HTMLInputElement>, value: string | number): void =>
                updateDraft(version.versionId, { name: String(value) })
              }
            />
          </div>
          <KuikaInput
            type="textarea"
            placeholder="What changed in this version?"
            value={draft.description}
            rows={3}
            resizable={false}
            onChange={(_e: React.ChangeEvent<HTMLTextAreaElement>, value: string | number): void =>
              updateDraft(version.versionId, { description: String(value) })
            }
          />
        </div>
      </KModalStepBody>

      <div className="kuika-wfe-version-details__actions">
        <KuikaButton
          type="text"
          disabled={isSaving}
          onClick={props.onDone}
          data-testid="workflowVersionDetailsSkipButton"
        >
          {isStepper ? "Skip all" : "Skip"}
        </KuikaButton>
        <div className="kuika-wfe-version-details__actions-nav">
          {isStepper && stepIndex > 0 && (
            <KuikaButton type="secondary" disabled={isSaving} onClick={(): void => setStepIndex(stepIndex - 1)}>
              Back
            </KuikaButton>
          )}
          {isStepper && !isLastStep ? (
            <KuikaButton type="primary" onClick={(): void => setStepIndex(stepIndex + 1)}>
              Next
            </KuikaButton>
          ) : (
            <KuikaButton type="primary" status={saveStatus} onClick={handleSave}>
              Save
            </KuikaButton>
          )}
        </div>
      </div>
    </>
  );
};

// Rendered once from an always-mounted host (the run button). It subscribes to the screen-design
// context directly, so it opens whenever an export reports freshly-minted versions. When the export
// also mailed executables, this modal is the ONLY post-export surface — the publish popover's
// "sent" message is suppressed (KOA-18257) and its mail notice renders here instead.
const WorkflowVersionDetailsModalBase = (props: IWithSDProps & IWithContextProp): JSX.Element => {
  const versions: INewExportVersion[] = props.sd.newExportVersionsToDetail ?? [];
  const showMailInfo: boolean = props.sd.newExportVersionsMailInfo;
  const userEmail: string | undefined = props.context?.userInfo?.username;
  const handleDone = (): void => props.sd.clearNewExportVersionsToDetail();

  return (
    <DefaultModal
      firstHeader="Describe new versions"
      showModal={versions.length > 0}
      closeModal={handleDone}
      width={480}
      dontRenderSpinner
    >
      {versions.length > 0 ? (
        <>
          {showMailInfo && (
            <div className="kuika-wfe-version-details__mail">
              <KuikaInfoBox
                color="blue"
                leftUnit={
                  <div className="kuika-flex--gap-xs">
                    <KuikaSvg name="mail" width={16} height={16} />
                    <KuikaSmallText>
                      We took flight. We will inform via your {userEmail} email address when we arrive at the
                      destination.
                    </KuikaSmallText>
                  </div>
                }
              />
            </div>
          )}
          <VersionDetailsForm versions={versions} onDone={handleDone} />
        </>
      ) : (
        <></>
      )}
    </DefaultModal>
  );
};

const workflowVersionDetailsModal = withContext<object>(withSD<object>(WorkflowVersionDetailsModalBase));
export { workflowVersionDetailsModal as WorkflowVersionDetailsModal };
